var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ConnectionStates } from '@shared/commons/ConnectionEnums';
import { Connection } from '@shared/commons/Connection';
import { CustomErrorClass, ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { ApplicationInfo } from '@shared/commons/ApplicationInfo';
import { UserManager } from './UserManager';
export class BaseStorageManager {
    get key() {
        return UserManager.getUserKey();
    }
    constructor(indexedDB, storeName) {
        this.SYNC_OUT_INTERVAL = 10 * 60 * 1000; // 10 minutes
        this.SYNC_IN_INTERVAL = 2 * 1000; // 2 seconds
        this.wasSyncedIn = true;
        this.wasSyncedOut = true;
        this.syncOutTimer = null;
        this.syncInTimer = null;
        this.connectionStateChange = () => __awaiter(this, void 0, void 0, function* () {
            const state = Connection.getState();
            switch (state) {
                case ConnectionStates.CONNECTED:
                    this.startIntervalSyncInOut();
                    break;
                case ConnectionStates.DISCONNECTED:
                case ConnectionStates.CONNECTION_LOST:
                    this.stopIntervalSyncInOut();
                    break;
            }
        });
        this.indexedDB = indexedDB;
        this.storeName = storeName;
    }
    // #endregion
    wasChanged() {
        this.wasSyncedIn = false;
        if (!ApplicationInfo.isExploreMode) {
            this.wasSyncedOut = false;
        }
    }
    initialize() {
        Connection.subscribeOnConnectStateChange(this.connectionStateChange);
    }
    syncInOut() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const [localResult, remoteResult] = yield Promise.all([this.fetchIn(), this.fetchOut()]);
                const hasLocal = localResult != null;
                const hasRemote = remoteResult != null;
                let data = null;
                if (hasLocal && !hasRemote) {
                    data = localResult.settings;
                }
                else if (!hasLocal && hasRemote) {
                    data = remoteResult.settings;
                }
                else if (hasLocal && hasRemote) {
                    if (localResult.updateTimeSpan > remoteResult.updateTimeSpan) {
                        data = localResult.settings;
                    }
                    else {
                        data = remoteResult.settings;
                    }
                }
                const preparedData = data ? yield this.deserializeDataAsync(data) : null;
                yield this.onRestoreAsync(preparedData);
            }
            catch (error) {
                console.error('Error in syncInOut', error);
            }
        });
    }
    startIntervalSyncInOut() {
        this.syncOutTimer = setInterval(() => {
            void this.syncOut();
        }, this.SYNC_OUT_INTERVAL);
        this.syncInTimer = setInterval(() => {
            void this.syncIn();
        }, this.SYNC_IN_INTERVAL);
    }
    stopIntervalSyncInOut() {
        if (this.syncOutTimer) {
            clearInterval(this.syncOutTimer);
            this.syncOutTimer = null;
        }
        if (this.syncInTimer) {
            clearInterval(this.syncInTimer);
            this.syncInTimer = null;
        }
    }
    fetchIn() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const localResult = yield this.indexedDB.get(this.storeName, this.key);
                return localResult;
            }
            catch (error) {
                console.error('Error fetching settings from local DB:', error);
                return null;
            }
        });
    }
    fetchOut() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const settingsData = yield this.fetchRemoteSettings();
                return settingsData;
            }
            catch (error) {
                console.error('Error fetching settings from server:', error);
                return null;
            }
        });
    }
    syncIn() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.wasSyncedIn && !ApplicationInfo.isExploreMode) {
                this.wasSyncedIn = true;
                const dataJson = this.serializeData();
                const dataResult = {
                    userId: this.key,
                    settings: dataJson,
                    updateTimeSpan: +new Date()
                };
                yield this.indexedDB.addOrUpdate(this.storeName, dataResult);
            }
        });
    }
    syncOut() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this.wasSyncedOut && !ApplicationInfo.isExploreMode) {
                    this.wasSyncedOut = true;
                    const settingsData = yield this.fetchIn();
                    if (settingsData) {
                        yield this.sendRemoteSettings(settingsData);
                    }
                }
            }
            catch (error) {
                const ex = new CustomErrorClass('BaseSettingsStorageManager syncOut error', 'save', 'save -> UserWebStorage.settings');
                ErrorInformationStorage.GetException(ex);
                ErrorInformationStorage.GetException(error);
            }
        });
    }
}
